import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, ArrowRight, ArrowLeft, Sparkles, Upload, Trash2 } from "lucide-react";

const DAYS = [
  { key: "mon", label: "Mo" },
  { key: "tue", label: "Di" },
  { key: "wed", label: "Mi" },
  { key: "thu", label: "Do" },
  { key: "fri", label: "Fr" },
  { key: "sat", label: "Sa" },
  { key: "sun", label: "So" },
];

export default function OnboardingWizard() {
  const { t, refresh, salon } = useAuth();
  const [step, setStep] = useState(0);
  const [theme, setTheme] = useState(null);
  const [avail, setAvail] = useState(null);
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState({ name: "", duration_minutes: 60, price: 50 });
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", role: "", email: "" });
  const [staffLimit, setStaffLimit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  // Localized step labels
  const STEPS = [
    { id: "brand", label: "Marke & Logo" },
    { id: "hours", label: "Öffnungszeiten" },
    { id: "services", label: t("service_plural") },
    { id: "staff", label: `Erste/r ${t("staff")}` },
    { id: "done", label: "Fertig" },
  ];

  useEffect(() => {
    Promise.all([api.get("/cms/theme"), api.get("/cms/availability"), api.get("/cms/services"), api.get("/cms/staff"), api.get("/cms/staff/limit")])
      .then(([th, a, s, st, lim]) => {
        setTheme(th.data || {});
        setAvail({ weekly: a.data.weekly || {}, exceptions: a.data.exceptions || {} });
        setServices(s.data || []);
        setStaff(st.data || []);
        setStaffLimit(lim?.data || null);
      }).catch(() => {});
    // ensure t() reflects the freshly-registered industry
    refresh && refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update default role placeholder once terminology is available
  // (handled inline via placeholder text — no state mutation in effect needed)

  if (!theme || !avail) return <div className="p-12">Lade...</div>;

  const next = () => { setErr(""); setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const prev = () => { setErr(""); setStep((s) => Math.max(s - 1, 0)); };

  const finish = async () => {
    await api.put("/cms/onboarding/complete");
    navigate("/cms");
  };

  const saveTheme = async () => {
    const { id, _id, salon_id, updated_at, ...payload } = theme;
    await api.put("/cms/theme", payload);
    next();
  };

  const saveHours = async () => {
    await api.put("/cms/availability", { weekly: avail.weekly, exceptions: avail.exceptions });
    next();
  };

  const addService = async () => {
    if (!newService.name) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/cms/services", { ...newService, active: true, staff_ids: [] });
      setServices([...services, data]);
      setNewService({ name: "", duration_minutes: 60, price: 50 });
    } catch (e) {
      setErr(e.response?.data?.detail || `Konnte ${t("service")} nicht anlegen`);
    } finally { setBusy(false); }
  };
  const removeService = async (id) => {
    if (!window.confirm(`${t("service")} wirklich entfernen?`)) return;
    try {
      await api.delete(`/cms/services/${id}`);
      setServices(services.filter((x) => x.id !== id));
    } catch (e) {
      setErr(e.response?.data?.detail || `Konnte ${t("service")} nicht löschen`);
    }
  };

  const addStaff = async () => {
    if (!newStaff.name) return;
    setBusy(true); setErr("");
    try {
      const role = (newStaff.role || "").trim() || t("stylist");
      const { data } = await api.post("/cms/staff", {
        name: newStaff.name.trim(),
        role,
        email: (newStaff.email || "").trim() || null,
        active: true,
      });
      setStaff([...staff, data]);
      setNewStaff({ name: "", role: "", email: "" });
      // Refresh limit info so the UI updates immediately
      try { const lim = await api.get("/cms/staff/limit"); setStaffLimit(lim.data); } catch { /* ignore */ }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "staff_limit_reached") {
        setErr(detail.message || "Paket-Limit für Mitarbeitende erreicht.");
      } else {
        setErr(typeof detail === "string" ? detail : `Konnte ${t("staff")} nicht anlegen`);
      }
    } finally { setBusy(false); }
  };
  const removeStaff = async (id) => {
    if (!window.confirm(`${t("staff")} wirklich entfernen?`)) return;
    try {
      await api.delete(`/cms/staff/${id}`);
      setStaff(staff.filter((x) => x.id !== id));
      try { const lim = await api.get("/cms/staff/limit"); setStaffLimit(lim.data); } catch { /* ignore */ }
    } catch (e) {
      setErr(e.response?.data?.detail || `Konnte ${t("staff")} nicht löschen`);
    }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post("/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setTheme({ ...theme, logo_url: data.url });
    // Sync into the header so the public site immediately shows the logo
    try {
      const { data: header } = await api.get("/cms/header");
      const { id: _hid, _id, salon_id: _hsid, updated_at: _hua, ...payload } = header || {};
      await api.put("/cms/header", { ...payload, logo_url: data.url });
    } catch (_e) { /* non-fatal: theme upload already succeeded */ }
  };

  const skipOnboarding = async () => {
    if (!window.confirm("Onboarding wirklich überspringen? Du kannst alle Einstellungen jederzeit später im CMS nachholen.")) return;
    try {
      await api.put("/cms/onboarding/complete");
    } catch (_e) { /* navigate even if marking fails */ }
    navigate("/cms");
  };

  const setHours = (day, idx, field, val) => {
    const wins = [...(avail.weekly[day] || [])];
    wins[idx] = { ...wins[idx], [field]: val };
    setAvail({ ...avail, weekly: { ...avail.weekly, [day]: wins } });
  };
  const toggleDayOpen = (day) => {
    const wins = avail.weekly[day] || [];
    const nextWins = wins.length === 0 ? [{ start: "09:00", end: "18:00" }] : [];
    setAvail({ ...avail, weekly: { ...avail.weekly, [day]: nextWins } });
  };

  return (
    <div className="min-h-screen p-6 lg:p-12" style={{ background: "var(--mv-bg)" }}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
            <p className="mv-label">Onboarding</p>
          </div>
          <button
            type="button"
            data-testid="onb-skip"
            onClick={skipOnboarding}
            className="text-xs hover:underline"
            style={{ color: "var(--mv-text-muted)" }}
          >
            Onboarding überspringen →
          </button>
        </div>
        <h1 className="font-display text-4xl mb-8">Lass uns {salon?.name ? <span style={{ color: "var(--mv-primary)" }}>{salon.name}</span> : t("salon")} einrichten</h1>

        <div className="flex items-center justify-between mb-10">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${i <= step ? "bg-[var(--mv-primary)] text-white" : "bg-[var(--mv-surface)] text-[var(--mv-text-muted)]"}`}>
                {i < step ? <Check className="w-4 h-4" strokeWidth={2} /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-0.5 mx-2" style={{ background: i < step ? "var(--mv-primary)" : "var(--mv-border)" }} />}
            </div>
          ))}
        </div>

        <div className="mv-card p-8 mv-fade-in">
          {step === 0 && (
            <div className="space-y-5">
              <p className="mv-label mb-1">Schritt 1 von 5</p>
              <h2 className="font-display text-2xl mb-2">Marke & Logo</h2>
              <p className="text-sm mb-6" style={{ color: "var(--mv-text-muted)" }}>Definiere deine Markenfarbe und lade dein Logo hoch.</p>
              <div>
                <label className="block text-xs mb-1.5">Logo</label>
                <div className="flex gap-3 items-center">
                  {theme.logo_url && <img src={theme.logo_url} alt="" className="h-14 object-contain border rounded" style={{ borderColor: "var(--mv-border)" }} />}
                  <label className="mv-btn-ghost text-sm cursor-pointer"><Upload className="w-3.5 h-3.5" /> Logo hochladen<input type="file" accept="image/*" className="hidden" onChange={uploadLogo} /></label>
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1.5">Primärfarbe</label>
                <div className="flex items-center gap-3">
                  <input type="color" data-testid="onb-primary-color" value={theme.primary_color || "#C87A5F"} onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })} className="w-14 h-12 rounded border cursor-pointer" />
                  <input className="mv-input" value={theme.primary_color || "#C87A5F"} onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          {step === 1 && (
            <div>
              <p className="mv-label mb-1">Schritt 2 von 5</p>
              <h2 className="font-display text-2xl mb-2">Öffnungszeiten</h2>
              <p className="text-sm mb-6" style={{ color: "var(--mv-text-muted)" }}>Wann hat dein Betrieb geöffnet?</p>
              <div className="space-y-3">
                {DAYS.map((d) => {
                  const w = avail.weekly[d.key] || [];
                  const open = w.length > 0;
                  return (
                    <div key={d.key} className="flex items-center gap-3">
                      <div className="w-12 text-sm font-medium">{d.label}</div>
                      <label className="text-xs"><input type="checkbox" checked={open} onChange={() => toggleDayOpen(d.key)} /> geöffnet</label>
                      {open && (
                        <>
                          <input type="time" className="mv-input text-sm w-28" value={w[0].start} onChange={(e) => setHours(d.key, 0, "start", e.target.value)} />
                          <span className="text-xs">bis</span>
                          <input type="time" className="mv-input text-sm w-28" value={w[0].end} onChange={(e) => setHours(d.key, 0, "end", e.target.value)} />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {step === 2 && (
            <div>
              <p className="mv-label mb-1">Schritt 3 von 5</p>
              <h2 className="font-display text-2xl mb-2">{t("service_plural")}</h2>
              <p className="text-sm mb-6" style={{ color: "var(--mv-text-muted)" }}>
                Welche {t("service_plural")} bietest du an? (Du kannst später jederzeit weitere hinzufügen.)
              </p>
              {err && step === 2 && <p data-testid="onb-error" className="text-sm mb-3 px-3 py-2 rounded bg-red-50 text-red-700">{err}</p>}
              <div className="space-y-2 mb-4">
                {services.map((s) => (
                  <div key={s.id} className="flex justify-between items-center p-3 rounded-lg border" style={{ borderColor: "var(--mv-border)" }} data-testid={`onb-service-row-${s.id}`}>
                    <span className="text-sm">{s.name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs" style={{ color: "var(--mv-text-muted)" }}>{s.duration_minutes} Min · {s.price?.toFixed(2)} €</span>
                      <button
                        data-testid={`onb-remove-service-${s.id}`}
                        onClick={() => removeService(s.id)}
                        className="p-1.5 rounded hover:bg-red-50"
                        title="Entfernen"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "#b91c1c" }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-[1fr_80px_80px_auto] gap-2">
                <input data-testid="onb-service-name" placeholder={`Name (z. B. ${t("service")})`} className="mv-input text-sm" value={newService.name} onChange={(e) => setNewService({ ...newService, name: e.target.value })} />
                <input type="number" placeholder="Min" className="mv-input text-sm" value={newService.duration_minutes} onChange={(e) => setNewService({ ...newService, duration_minutes: parseInt(e.target.value) || 30 })} />
                <input type="number" step="0.01" placeholder="€" className="mv-input text-sm" value={newService.price} onChange={(e) => setNewService({ ...newService, price: parseFloat(e.target.value) || 0 })} />
                <button data-testid="onb-add-service" onClick={addService} disabled={busy} className="mv-btn-ghost text-sm">+ Hinzufügen</button>
              </div>
            </div>
          )}
          {step === 3 && (
            <div>
              <p className="mv-label mb-1">Schritt 4 von 5</p>
              <h2 className="font-display text-2xl mb-2">{t("staff_plural")}</h2>
              <p className="text-sm mb-3" style={{ color: "var(--mv-text-muted)" }}>
                Füge dein/e erste/n {t("staff")} hinzu (oder überspringe). Du kannst später jederzeit weitere {t("staff_plural")} ergänzen.
              </p>
              {staffLimit && (
                <p
                  className="text-xs mb-4 px-3 py-2 rounded"
                  data-testid="onb-staff-limit-info"
                  style={{
                    background: staffLimit.unlimited ? "var(--mv-surface)" : (staffLimit.remaining === 0 ? "#fef2f2" : "rgba(20,184,166,0.06)"),
                    color: staffLimit.remaining === 0 ? "#b91c1c" : "var(--mv-text-muted)",
                  }}
                >
                  {staffLimit.unlimited
                    ? `Dein Paket erlaubt unbegrenzt viele ${t("staff_plural")}.`
                    : `${staffLimit.current} / ${staffLimit.limit} ${t("staff_plural")} ${staffLimit.remaining === 0 ? "— Limit deines Pakets erreicht. Upgrade nötig für weitere." : `verfügbar (${staffLimit.remaining} frei)`}`}
                </p>
              )}
              {err && step === 3 && <p data-testid="onb-error" className="text-sm mb-3 px-3 py-2 rounded bg-red-50 text-red-700">{err}</p>}
              <div className="space-y-2 mb-4">
                {staff.map((s) => (
                  <div key={s.id} className="flex justify-between items-center p-3 rounded-lg border" style={{ borderColor: "var(--mv-border)" }} data-testid={`onb-staff-row-${s.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.name}</p>
                      {s.email && <p className="text-[11px] truncate" style={{ color: "var(--mv-text-muted)" }}>{s.email}</p>}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs" style={{ color: "var(--mv-text-muted)" }}>{s.role}</span>
                      <button
                        data-testid={`onb-remove-staff-${s.id}`}
                        onClick={() => removeStaff(s.id)}
                        className="p-1.5 rounded hover:bg-red-50"
                        title="Entfernen"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "#b91c1c" }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2">
                <input data-testid="onb-staff-name" placeholder="Name" className="mv-input text-sm" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} />
                <input data-testid="onb-staff-role" placeholder={`Rolle (${t("stylist")})`} className="mv-input text-sm" value={newStaff.role} onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })} />
                <input data-testid="onb-staff-email" type="email" placeholder="E-Mail (optional)" className="mv-input text-sm" value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} />
                <button
                  data-testid="onb-add-staff"
                  onClick={addStaff}
                  disabled={busy || (staffLimit && !staffLimit.unlimited && staffLimit.remaining === 0)}
                  className="mv-btn-ghost text-sm"
                  title={staffLimit && !staffLimit.unlimited && staffLimit.remaining === 0 ? "Paket-Limit erreicht" : ""}
                >
                  + Hinzufügen
                </button>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="text-center py-8">
              <Sparkles className="w-12 h-12 mx-auto mb-4" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
              <h2 className="font-display text-3xl mb-2">Bereit für {t("booking_plural")}!</h2>
              <p className="text-sm mb-8" style={{ color: "var(--mv-text-muted)" }}>
                {salon?.name ? `${salon.name} ist` : `Dein ${t("salon")} ist`} eingerichtet. Schau dich im CMS um oder veröffentliche deine erste Seite.
              </p>
              <button data-testid="onb-finish" onClick={finish} className="mv-btn">Zum Dashboard <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></button>
            </div>
          )}

          {step < 4 && (
            <div className="flex justify-between mt-8 pt-6 border-t" style={{ borderColor: "var(--mv-border)" }}>
              <button onClick={prev} disabled={step === 0} className="mv-btn-ghost text-sm disabled:opacity-30"><ArrowLeft className="w-3.5 h-3.5" /> Zurück</button>
              {step === 0 && <button data-testid="onb-next-1" onClick={saveTheme} className="mv-btn text-sm">Weiter <ArrowRight className="w-4 h-4" /></button>}
              {step === 1 && <button data-testid="onb-next-2" onClick={saveHours} className="mv-btn text-sm">Weiter <ArrowRight className="w-4 h-4" /></button>}
              {step === 2 && <button data-testid="onb-next-3" onClick={next} className="mv-btn text-sm">Weiter <ArrowRight className="w-4 h-4" /></button>}
              {step === 3 && <button data-testid="onb-next-4" onClick={next} className="mv-btn text-sm">Weiter <ArrowRight className="w-4 h-4" /></button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
